import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { OnboardingService } from '../../../onboarding/services/onboarding.service';
import { CustomerStoreService } from '../../services/customer-store.service';
import { CustomerService } from '../../services/customer.service';
import { HandsetService } from '../../services/handset.service';
import { ModalService } from '../../services/modal.service';
import { ToolbarService } from '../../services/toolbar.service';
import { EmailVerificationService } from '../../../shared/services/auth/email-verification.service';
import { Group } from '../../models/customer-test-group.model';
import { AppConfig } from '../../services/app-config.service';
import { MainNotificationService } from '../../services/main-notification.service';
import { AccountConsentsService } from '../../../account-consents/components/account-consents.service';
import { MatDialog } from '@angular/material/dialog';
import { OnboardingComponent } from '../../../onboarding/containers/onboarding/onboarding.component';
import { AuthStoreService } from '../../../shared/services/auth/auth-store.service';
import { SavedUrlService } from '../../services/saved-url.service';
import { ContentDataService } from '../../../liabilities/services/content-data.service';
import { WarningStoreService } from '../../../auth/services/warning-store.service';
import { OnboardingStep } from '../../../onboarding/services/onboarding-store.service';

@Component({
standalone: false,
  selector: 'app-content',
  styleUrls: ['./content.component.scss'],
  templateUrl: './content.component.html',
})
export class ContentComponent implements OnInit, OnDestroy {
  @ViewChild('drawer', { static: true }) public drawer: MatSidenav;

  public unsubscribe = new Subject();
  public containerStyle: { top: string };

  constructor(
    public toolbarService: ToolbarService,
    public handsetService: HandsetService,
    public appConfig: AppConfig,
    private router: Router,
    private onboardingService: OnboardingService,
    private customerStoreService: CustomerStoreService,
    private modalService: ModalService,
    private customerService: CustomerService,
    private emailVerificationService: EmailVerificationService,
    private changeDetectorRef: ChangeDetectorRef,
    private mainNotificationService: MainNotificationService,
    private accountConsentsService: AccountConsentsService,
    public dialog: MatDialog,
    public authService: AuthStoreService,
    public contentStoreService: ContentDataService,
    public savedUrlService: SavedUrlService,
    private warningStore: WarningStoreService,
  ) {
  }

  public ngOnInit(): void {
    this.onboardingRedirect();

    this.toolbarService.drawerToggle.subscribe(() => this.drawer.toggle());

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter(() => this.handsetService.isHandset),
        tap(() => this.drawer.close()),
        takeUntil(this.unsubscribe),
      )
      .subscribe();

    this.handsetService.handsetChanges
      .pipe(
        filter((isHandset) => !isHandset),
        tap(() => this.drawer.open('program')),
        takeUntil(this.unsubscribe),
      )
      .subscribe();

    // this.customerService.showCustomerAccountInfoModal();
    this.mainNotificationService.mainNotificationHeightAsObservable().subscribe((height: number) => {
      this.onMainNotificationResize(height);
    });
  }

  private onboardingRedirect() {
    if (this.onboardingService.shouldNavigate()) {
      if (this.customerStoreService.state.testGroup.mgmTestGroup === Group.GROUP_B) {
        this.emailVerificationService.redirectAfterEmailVerification();
        return;
      }

      this.showOnboardingQuestionnaireForGroup();
      this.openOnboardingDialog(this.onboardingService.step());
    }
  }

  public onSwipeLeft(): void {
    if (this.handsetService.isHandset) {
      this.drawer.close();
    }
  }

  public onSwipeRight(event): void {
    if (this.handsetService.isHandset) {
      const { x } = this.getStartPosition(event);
      if (x >= 0 && x <= 50) {
        this.drawer.open();
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
  }

  private getStartPosition(e): any {
    const { deltaX } = e;
    const { deltaY } = e;
    const finalX = e.srcEvent.pageX || e.srcEvent.screenX || 0;
    const finalY = e.srcEvent.pageY || e.srcEvent.screenY || 0;

    return {
      x: finalX - deltaX,
      y: finalY - deltaY,
    };
  }

  private showOnboardingQuestionnaireForGroup() {
    const allowedSteps = ['WELCOME'];
    if (allowedSteps.includes(this.onboardingService.step())) {
      this.emailVerificationService.showOnboardingQuestionnaireForGroup();
    }
  }

  public onMainNotificationResize(event: number): void {
    this.containerStyle = event
      ? {
        top: event + 56 + 'px',
      }
      : null;
    this.changeDetectorRef.detectChanges();
  }

  public showToolbarBanner(): boolean {
    return (
      !!this.appConfig?.config?.endOfServiceDate ||
      this.appConfig?.config?.applicationReadOnlyMode ||
      (this.appConfig?.config?.migration?.bannerEnabled && this.accountConsentsService.migrationConsentActive) ||
      (!this.appConfig?.config?.payment?.autoPayment?.enabled &&
        this.customerStoreService.hasConfiguredAutomaticPayments()) ||
      this.appConfig?.config?.features?.displayCardMigrationIsUnderWay
    );
  }

  openOnboardingDialog(step: OnboardingStep): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dialogRef = this.dialog.open(OnboardingComponent, {
      minWidth: '320px',
      width: this.handsetService.isMobile ? '100vw' : '600px',
      height: this.handsetService.isMobile ? '100vh' : '550px',
      maxWidth: '100%',
      maxHeight: '100vh',
      disableClose: true,
      data: {
        step: step,
      },
    });
  }

  public onSidenavClick() {
    if (this.drawer.opened && this.handsetService.isMobile) {
      this.drawer.close();
    }
  }

  public logout() {
    this.authService.logout()
      .subscribe(() => {
        this.savedUrlService.clear();
      });

  }

  get showRedBorder() {
    return this.warningStore.showRedBorder();
  }

}
